import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaChevronRight, FaChevronLeft, FaClock, FaHome, FaLink, FaFont, FaCodeBranch, FaCode, FaExchangeAlt, FaFingerprint, FaKey, FaCalendarAlt, FaSearch, FaInfoCircle, FaEnvelope, FaShieldAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import styles from './Sidebar.module.css';

const Sidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = location.pathname.match(/^\/(zh|en|es|ko|ja)(?=\/|$)/)?.[1] ?? 'en';
  const pathWithoutLang = location.pathname.replace(/^\/(zh|en|es|ko|ja)(?=\/|$)/, '') || '/';

  const withLang = (path: string) => {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return normalized === '/' ? `/${currentLang}` : `/${currentLang}${normalized}`;
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const isActive = (path: string) => {
    return pathWithoutLang === path;
  };

  const switchLanguage = (lang: 'zh' | 'en' | 'es' | 'ko' | 'ja') => {
    const targetLocale =
      lang === 'zh' ? 'zh-CN' : lang === 'es' ? 'es-ES' : lang === 'ko' ? 'ko-KR' : lang === 'ja' ? 'ja-JP' : 'en-US';
    void i18n.changeLanguage(targetLocale);
    const targetPath = pathWithoutLang === '/' ? `/${lang}` : `/${lang}${pathWithoutLang}`;
    const target = `${targetPath}${location.search}${location.hash}`;
    navigate(target);
  };

  return (
    <div className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''}`}>
      <div className={styles['sidebar-header']}>
        <button
          type="button"
          onClick={toggleSidebar}
          className={styles['sidebar-toggle-btn']}
          aria-label={isCollapsed ? t('sidebar.toggleExpand') : t('sidebar.toggleCollapse')}
        >
          {isCollapsed ? (
            <FaChevronRight size={18} />
          ) : (
            <FaChevronLeft size={18} />
          )}
        </button>
      </div>

      <nav className={styles['sidebar-nav']}>
        {!isCollapsed && (
          <div className={styles['language-switcher']}>
            <span className={styles['language-switcher-label']}>{t('sidebar.language')}</span>
            <select
              className={styles['language-switcher-select']}
              value={currentLang}
              onChange={(e) => switchLanguage(e.target.value as 'zh' | 'en' | 'es' | 'ko' | 'ja')}
              aria-label={t('sidebar.language')}
            >
              <option value="zh">中文</option>
              <option value="en">English</option>
              <option value="es">Espanol</option>
              <option value="ko">한국어</option>
              <option value="ja">日本語</option>
            </select>
          </div>
        )}
        <Link 
          to={withLang('/')}
          className={`${styles['sidebar-link']} ${isActive('/') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaHome size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.home')}</span>}
        </Link>
        <Link 
          to={withLang('/timestamp-converter')}
          className={`${styles['sidebar-link']} ${isActive('/timestamp-converter') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaClock size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.timestampConverter')}</span>}
        </Link>
        <Link
          to={withLang('/url-encoder-decoder')}
          className={`${styles['sidebar-link']} ${isActive('/url-encoder-decoder') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaLink size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.urlCodec')}</span>}
        </Link>
        <Link
          to={withLang('/string-stats')}
          className={`${styles['sidebar-link']} ${isActive('/string-stats') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaFont size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.stringStats')}</span>}
        </Link>
        <Link
          to={withLang('/string-diff')}
          className={`${styles['sidebar-link']} ${isActive('/string-diff') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaCodeBranch size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.stringDiff')}</span>}
        </Link>
        <Link
          to={withLang('/json-formatter')}
          className={`${styles['sidebar-link']} ${isActive('/json-formatter') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaCode size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.jsonFormatter')}</span>}
        </Link>
        <Link
          to={withLang('/base64-codec')}
          className={`${styles['sidebar-link']} ${isActive('/base64-codec') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaExchangeAlt size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.base64Codec')}</span>}
        </Link>
        <Link
          to={withLang('/hash-calculator')}
          className={`${styles['sidebar-link']} ${isActive('/hash-calculator') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaFingerprint size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.hashCalculator')}</span>}
        </Link>
        <Link
          to={withLang('/jwt-parser')}
          className={`${styles['sidebar-link']} ${isActive('/jwt-parser') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaKey size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.jwtParser')}</span>}
        </Link>
        <Link
          to={withLang('/cron-parser')}
          className={`${styles['sidebar-link']} ${isActive('/cron-parser') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaCalendarAlt size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.cronParser')}</span>}
        </Link>
        <Link
          to={withLang('/regex-tester')}
          className={`${styles['sidebar-link']} ${isActive('/regex-tester') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaSearch size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.regexTester')}</span>}
        </Link>
        <Link
          to={withLang('/about')}
          className={`${styles['sidebar-link']} ${isActive('/about') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaInfoCircle size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.about')}</span>}
        </Link>
        <Link
          to={withLang('/contact')}
          className={`${styles['sidebar-link']} ${isActive('/contact') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaEnvelope size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.contact')}</span>}
        </Link>
        <Link
          to={withLang('/privacy')}
          className={`${styles['sidebar-link']} ${isActive('/privacy') ? styles['sidebar-link-active'] : ''}`}
        >
          <FaShieldAlt size={20} className={styles['sidebar-link-icon']} />
          {!isCollapsed && <span className={styles['sidebar-link-text']}>{t('sidebar.privacy')}</span>}
        </Link>
      </nav>
    </div>
  );
};

export default Sidebar; 